import {cardsInDeck, valuableCardValue} from '@kapitalist/contract/ruleset'
import type {
  TDirectionalRentCardData,
  TStandardPropertyCard,
  TUniversalRentCardData,
} from '@kapitalist/contract/types'
import type {TMillions} from '@kapitalist/contract/types/millions'
import {unreachableCase} from '@kapitalist/utils/exhaustive'
import {createUnsafe, type NEList} from '@kapitalist/utils/NEList'
import {lowerFirst} from '@kapitalist/utils/strings'
import type {TSkinData} from '~/game/skins/types'
import type {TRentCardData} from '~/game/types'
import {allStandardPropertyTypes} from '~/game/types'
import type {TAvailableMovesData} from '~/models/game/Card'
import Card from '~/models/game/Card'
import MajorActionDirectionalRent from '~/models/game/MajorActions/MajorActionDirectionalRent'
import MajorActionPlayAsCash from '~/models/game/MajorActions/MajorActionPlayAsCash'
import MajorActionUniversalRent from '~/models/game/MajorActions/MajorActionUniversalRent'
import type {AnyMajorAction} from '~/models/game/types'

export default abstract class RentCard<
  CardType extends TRentCardData,
> extends Card<CardType> {
  public value: TMillions

  constructor(data: CardType) {
    super(data)
    this.value = valuableCardValue[data.type]
  }

  public fileName(skinData: TSkinData): string {
    return `rent_${
      this.type === 'directionalRent'
        ? skinData.directionalRent === 'identical'
          ? 'directionalWild'
          : `directionalWild${this.version % cardsInDeck[this.type]}`
        : lowerFirst(this.type.slice(4)) // rentLightBlueBrown -> lightBlueBrown
    }`
  }

  // "Which colors does this rent card charge rent for?"
  abstract standardPropertyTypesSet(): Set<TStandardPropertyCard>
  abstract standardPropertyTypesNEList(): NEList<TStandardPropertyCard>
}

export class UniversalRentCard extends RentCard<TUniversalRentCardData> {
  standardPropertyTypesSet(): Set<TStandardPropertyCard> {
    return new Set<TStandardPropertyCard>(this.standardPropertyTypesNEList())
  }

  standardPropertyTypesNEList(): NEList<TStandardPropertyCard> {
    switch (this.type) {
      case 'rentDarkBlueGreen':
        return ['darkBlue', 'green']
      case 'rentLightBlueBrown':
        return ['lightBlue', 'brown']
      case 'rentPinkOrange':
        return ['pink', 'orange']
      case 'rentRedYellow':
        return ['red', 'yellow']
      case 'rentUtilityRailroad':
        return ['utility', 'railroad']
      default:
        return unreachableCase(this.type)
    }
  }

  public availableMovesFromHand({
    playerGamePosition,
    portfolio,
  }: TAvailableMovesData): (MajorActionUniversalRent | MajorActionPlayAsCash)[] {
    const asCash = new MajorActionPlayAsCash({
      type: 'playAsCash',
      card: this.datasource,
    })

    if (!playerGamePosition.opponentPortfoliosWithValue().length) {
      return [asCash]
    }

    return [
      ...portfolio
        .standardNeighborhoods()
        .filter(pn => this.standardPropertyTypesSet().has(pn.neighborhood))
        .map(
          pn =>
            new MajorActionUniversalRent(
              {
                card: this.datasource,
                portfolioNeighborhoodId: pn.id,
                type: this.type,
                millionsOwed: pn.rent(),
              },
              portfolio
            )
        ),
      asCash,
    ]
  }
}

export class DirectionalRentCard extends RentCard<TDirectionalRentCardData> {
  standardPropertyTypesNEList(): NEList<TStandardPropertyCard> {
    return createUnsafe([...allStandardPropertyTypes])
  }
  standardPropertyTypesSet(): Set<TStandardPropertyCard> {
    return allStandardPropertyTypes
  }

  public availableMovesFromHand({
    playerGamePosition,
    portfolio,
  }: TAvailableMovesData): AnyMajorAction[] {
    return [
      ...portfolio
        .standardNeighborhoods()
        .filter(pn => this.standardPropertyTypesSet().has(pn.neighborhood))
        .map(pn =>
          playerGamePosition.opponents
            .filter(o => o.portfolio.totalValue() > 0)
            .map(
              opponent =>
                new MajorActionDirectionalRent(
                  {
                    minorActorPlayerId: opponent.id,
                    card: this.datasource,
                    portfolioNeighborhoodId: pn.id,
                    type: 'directionalRent',
                    millionsOwed: pn.rent(),
                  },
                  portfolio
                )
            )
        )
        .flat(1),
      new MajorActionPlayAsCash({
        type: 'playAsCash',
        card: this.datasource,
      }),
    ]
  }
}
